import { useState, useEffect, useCallback } from 'react';
import { Rocket, Users, Film, Play, Pause } from 'lucide-react';
import useGameStore from '@store/useGameStore';
import useCinematicStore from '@store/useCinematicStore';
import DeployableUnitsPanel from '@components/game/panels/DeployableUnitsPanel';
import DeployedUnitsTab from '@components/game/panels/DeployedUnitsTab';
import CinematicPanel from '@components/game/panels/CinematicPanel';
import Slider from '@components/ui/general/Slider';
import './BottomDrawerContent.css';

export default function BottomDrawerContent() {
  const [activeTab, setActiveTab] = useState('deploy');

  // Get game phase info
  const isDeployPhase = useGameStore((state) => state.isDeployPhase);
  const isResolutionPhase = useGameStore((state) => state.isResolutionPhase);

  // Cinematic controls state
  const isAutoPlay = useCinematicStore((state) => state.isAutoPlay) || false;
  const playbackSpeed = useCinematicStore((state) => state.playbackSpeed) || 1;
  const isAnimating = useCinematicStore((state) => state.isAnimating);
  const setAutoPlay = useCinematicStore((state) => state.setAutoPlay);
  const setPlaybackSpeed = useCinematicStore((state) => state.setPlaybackSpeed);
  const executeCard = useCinematicStore((state) => state.executeCard);
  const resetCinematic = useCinematicStore((state) => state.reset);

  // Play/Pause handler
  const handlePlayPause = useCallback(() => {
    if (isAutoPlay) {
      setAutoPlay(false);
    } else {
      setAutoPlay(true);
      if (!isAnimating) {
        executeCard();
      }
    }
  }, [isAutoPlay, isAnimating, setAutoPlay, executeCard]);

  // Auto-switch tab based on game phase + auto-start cinematic
  useEffect(() => {
    if (isDeployPhase) {
      setActiveTab('deploy');
    } else if (isResolutionPhase) {
      setActiveTab('cinematic');
      // Auto-start cinematic playback after animations settle
      resetCinematic();
      const autoPlayTimer = setTimeout(() => {
        setAutoPlay(true);
        executeCard();
      }, 500); // Delay to let entrance animations complete
      return () => clearTimeout(autoPlayTimer);
    } else {
      setActiveTab('units');
    }
  }, [isDeployPhase, isResolutionPhase, resetCinematic, setAutoPlay, executeCard]);

  const tabs = [
    { id: 'deploy', label: 'Deploy', icon: Rocket },
    { id: 'units', label: 'Units', icon: Users },
    { id: 'cinematic', label: 'Cinematic', icon: Film },
  ];

  return (
    <div className="bottom-drawer-content">
      {/* Title bar at top */}
      <div className="bottom-drawer-header">
        <span key={activeTab} className="bottom-drawer-title">
          {tabs.find(t => t.id === activeTab)?.label}
        </span>

        {/* Cinematic controls - only show when cinematic tab is active */}
        {activeTab === 'cinematic' && isResolutionPhase && (
          <div className="cinematic-header-controls">
            <span className="speed-text">{playbackSpeed}x</span>
            <Slider
              variant="mini"
              min={0.5}
              max={3}
              step={0.5}
              value={playbackSpeed}
              onChange={setPlaybackSpeed}
              label="Playback speed"
            />
            <button
              className="play-btn button-enhanced"
              onClick={handlePlayPause}
              aria-label={isAutoPlay ? "Pause" : "Play"}
            >
              {isAutoPlay ? <Pause size={14} /> : <Play size={14} />}
            </button>
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="bottom-drawer-main">
        <div className="bottom-drawer-tabs">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const IconComponent = tab.icon;

            return (
              <button
                key={tab.id}
                className={`bottom-drawer-tab ${isActive ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <IconComponent size={18} />
              </button>
            );
          })}
        </div>

        <div className="bottom-drawer-panel">
          {activeTab === 'deploy' && <DeployableUnitsPanel />}
          {activeTab === 'units' && <DeployedUnitsTab />}
          {activeTab === 'cinematic' && <CinematicPanel />}
        </div>
      </div>
    </div>
  );
}
